import React, { useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { Spinner } from "reactstrap"
import Skeleton from "react-loading-skeleton"
import { ChevronLeft, ChevronRight, SlidersHorizontal, SearchX, AlertTriangle, X, HeartCrack } from "lucide-react"

import useTopInfluencers, { INFLUENCER_STATUS } from "../hooks/useTopInfluencers"
import { removeInfluencerFavourite } from "../influencerFavouritesApi"
import { DEFAULT_FILTERS_INFLUENCER, INFLUENCER_SORT_OPTIONS, INFLUENCER_PAGE_SIZE, INFLUENCER_MODE } from "../constants"
import { countActiveFilters } from "../utils"
import { buildInfluencerSearchParams, parseInfluencerSearchParams } from "../urlState"
import { useAnalytics, ANALYTICS_EVENTS } from "../../../analytics"
import InfluencerCard from "./InfluencerCard"
import InfluencerFiltersPanel from "./InfluencerFiltersPanel"
import InfluencerPlatformToggle from "./InfluencerPlatformToggle"
import InfluencerModeToggle from "./InfluencerModeToggle"
import SortDropdown from "./SortDropdown"
import AppSearchBar from "./AppSearchBar"

/**
 * The Influencer Explorer's main content - the cross-platform creator
 * leaderboard (TOP_INFLUENCER_FILTERS.md). Never requires an app to be
 * selected; `appId`/`appName` seed an optional, removable narrowing filter
 * for a caller that wants to deep-link into "influencers who've covered this
 * app" (e.g. from an app's own dashboard) - the standalone
 * `/influencer-explorer` route itself doesn't pass either, since the
 * AppSearchBar below lets a user pick one themselves.
 */
const InfluencerSection = ({ appId, appName }) => {
    // The URL is the source of truth for every bit of state below - read
    // once on mount (urlState.js), then kept in sync as the user changes
    // anything (the effect near the bottom of this component). This is what
    // makes "click a card, view its profile, hit Back" restore exactly the
    // list you were looking at instead of resetting to defaults: the
    // profile screen's Back link returns to this exact URL (InfluencerCard
    // captures it when navigating away), and remounting here re-parses it.
    // A caller-provided `appId`/`appName` (deep-linking in from elsewhere)
    // only seeds the initial state when the URL itself carries no filters
    // yet - once the URL has state of its own, it wins.
    const { trackEvent } = useAnalytics()
    const [searchParams, setSearchParams] = useSearchParams()
    const initialUrlState = useMemo(() => parseInfluencerSearchParams(searchParams), []) // eslint-disable-line react-hooks/exhaustive-deps
    const urlHadState = useMemo(() => searchParams.toString().length > 0, []) // eslint-disable-line react-hooks/exhaustive-deps

    const [filters, setFilters] = useState(() => (
        urlHadState || !appId ? initialUrlState.filters : { ...DEFAULT_FILTERS_INFLUENCER, appId }
    ))
    const [sort, setSort] = useState(() => initialUrlState.sort)
    const [page, setPage] = useState(() => initialUrlState.page)
    const [showFilters, setShowFilters] = useState(false)
    const [navigating, setNavigating] = useState(null)
    // "All Influencers" (the ranking) vs "My Favourites" - same
    // grid/pagination/filters underneath, INFLUENCER_MODE.FAVOURITES just
    // points useTopInfluencers at the favourites endpoint instead
    // (INFLUENCER_FAVOURITES_API.md §4: identical filter/sort contract).
    const [mode, setMode] = useState(() => initialUrlState.mode)
    const [dismissedMissing, setDismissedMissing] = useState(false)
    const resultsTopRef = useRef(null)
    // Drives the "Filtering to <app>" chip's label. Kept as its own bit of
    // state (rather than reading `filters.appId` alone) so the chip can show
    // a real name once picked via AppSearchBar, not just the raw numeric id -
    // falls back to the id itself if `filters.appId` was set some other way
    // (e.g. pasted directly into the filters panel's "App ID" field). Restored
    // from the URL's own `app_name` where present, so the chip reads "App:
    // Gorilla Tag" again after a round trip through the profile screen
    // rather than falling back to the bare id.
    const [selectedApp, setSelectedApp] = useState(() => {
        if (urlHadState && initialUrlState.filters.appId) {
            return { item_id: initialUrlState.filters.appId, name: initialUrlState.appName }
        }
        return appId && appName ? { item_id: appId, name: appName } : null
    })

    // Keeps the chip's name in sync with the "App ID" field inside the
    // filters panel - if that field is hand-edited or cleared, the name we
    // were showing no longer applies, so drop back to showing the raw id (or
    // nothing, once cleared).
    useEffect(() => {
        if (selectedApp && String(selectedApp.item_id) !== String(filters.appId)) setSelectedApp(null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.appId])

    const { data, status, error, isFetching, reload } = useTopInfluencers({
        source: mode, page, pageSize: INFLUENCER_PAGE_SIZE, sort, filters,
    })

    // One-way sync, state -> URL: every change here (a filter, the sort, the
    // page, the mode, the picked app's name) is mirrored onto the query
    // string with `replace` rather than `push`, so tweaking filters doesn't
    // pile up browser-history entries - the address bar just always reflects
    // "the view currently on screen", which is what a returning "Back" link
    // needs to find. The URL is only ever *read* on mount (the useState
    // initializers above), never re-parsed here, so this can't loop.
    useEffect(() => {
        setSearchParams(
            buildInfluencerSearchParams({ filters, sort, page, mode, appName: selectedApp?.name }),
            { replace: true }
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(filters), sort, page, mode, selectedApp?.name])

    const handleModeChange = (nextMode) => {
        if (nextMode === mode) return
        trackEvent(ANALYTICS_EVENTS.FEATURE.INFLUENCER_MODE_CHANGED, { mode: nextMode })
        setMode(nextMode)
        setSort(nextMode === INFLUENCER_MODE.FAVOURITES ? "saved" : "audience")
        setPage(1)
        setDismissedMissing(false)
    }

    // §7 - a saved influencer can leave the store (reclassified as an
    // official account on refresh); those entries are dropped from the page
    // and returned whole in `missing_entries` since they're on no page a
    // client could otherwise reach them from. `platform_key` (not
    // `username`) is what still resolves them - the one identifier DELETE
    // doesn't check the store to confirm.
    const handleClearMissingEntry = async (entry) => {
        try {
            await removeInfluencerFavourite({ platform: entry.platform, platformKey: entry.platform_key })
            reload()
        } catch {
            // Leave it listed - the user can try again from the same banner.
        }
    }

    // Platform now has its own always-visible toggle in the toolbar
    // (InfluencerPlatformToggle), styled to match PlatformTabs - so, same as
    // that video-tab switcher sitting entirely outside FiltersPanel's own
    // count, it's excluded from the "Filters (N)" badge to avoid a filter
    // the user can already see counting itself twice.
    const activeFilterCount = useMemo(() => countActiveFilters({ ...filters, platform: [] }), [filters])

    const handlePlatformFilterChange = (nextPlatforms) => {
        trackEvent(ANALYTICS_EVENTS.FEATURE.INFLUENCER_EXPLORER_FILTERED, {
            filter_type: "platform",
            platforms: Array.isArray(nextPlatforms) ? nextPlatforms.join(",") : nextPlatforms,
        })
        setFilters(f => ({ ...f, platform: nextPlatforms }))
        setPage(1)
    }

    useEffect(() => {
        if (!isFetching) setNavigating(null)
    }, [isFetching])

    const scrollToResultsTop = () => {
        resultsTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    }

    const handleFiltersChange = (next) => {
        Object.keys(next).forEach((key) => {
            const prevValue = filters[key]
            const nextValue = next[key]
            const changed = Array.isArray(prevValue) || Array.isArray(nextValue)
                ? JSON.stringify(prevValue) !== JSON.stringify(nextValue)
                : prevValue !== nextValue
            if (!changed) return
            const value = typeof nextValue === "object" && nextValue !== null ? JSON.stringify(nextValue) : nextValue
            trackEvent(ANALYTICS_EVENTS.FEATURE.INFLUENCER_EXPLORER_FILTERED, { filter_type: key, value })
        })
        setFilters(next)
        setPage(1)
    }

    const handleClearFilters = () => {
        trackEvent(ANALYTICS_EVENTS.FEATURE.INFLUENCER_EXPLORER_FILTER_CLEARED, { filter_type: "all" })
        setFilters(DEFAULT_FILTERS_INFLUENCER)
        setSelectedApp(null)
        setPage(1)
    }

    const handleSelectApp = (app) => {
        const id = String(app.item_id || app._id)
        trackEvent(ANALYTICS_EVENTS.FEATURE.INFLUENCER_EXPLORER_FILTERED, { filter_type: "app", app_id: id, app_name: app.name })
        setSelectedApp({ item_id: id, name: app.name })
        setFilters(f => ({ ...f, appId: id }))
        setPage(1)
    }

    const handleClearAppFilter = () => {
        trackEvent(ANALYTICS_EVENTS.FEATURE.INFLUENCER_EXPLORER_FILTER_CLEARED, { filter_type: "app" })
        setSelectedApp(null)
        setFilters(f => ({ ...f, appId: "" }))
        setPage(1)
    }

    const handleSortChange = (value) => {
        trackEvent(ANALYTICS_EVENTS.FEATURE.INFLUENCER_EXPLORER_FILTERED, { filter_type: "sort", sort: value })
        setSort(value)
        setPage(1)
    }

    const influencers = data?.influencers || []
    // §1: `total_influencers` is the filtered count the pager pages; an empty
    // filtered result reads correctly against `total_influencers_unfiltered`
    // ("your filters excluded everything" vs "discovery hasn't run yet").
    const isFiltered = Object.keys(data?.filters_applied || {}).length > 0

    return (
        <div style={{ background: "#21222d", borderRadius: "16px", padding: "24px" }}>
            {/* Compact "filter by app" search - a friendlier way in than
                pasting a raw App ID into the filters panel. Picking a result
                sets the same `filters.appId` the panel's own field does. The
                "All Influencers / My Favourites" toggle sits in this row,
                right of the search bar, rather than in the toolbar below -
                that row already carries Platform/Filters/Sort, and mode
                (which endpoint the whole list comes from) reads as a
                different kind of control than those in-list filters. */}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
                <div style={{ flex: "1 1 320px", minWidth: 0 }}>
                    {/* The applied app shows inside the input itself (falling
                        back to the raw id if it was set via the filters
                        panel); the input's own X clears it. */}
                    <AppSearchBar
                        onSelectApp={handleSelectApp}
                        selectedAppName={filters.appId ? (selectedApp?.name || String(filters.appId)) : ""}
                        onClear={handleClearAppFilter}
                    />
                </div>
                <InfluencerModeToggle value={mode} onChange={handleModeChange} />
            </div>

            {/* Toolbar: counts / sort / filters */}
            <div ref={resultsTopRef} className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3"
                style={{ paddingBottom: "16px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                <div style={{ fontSize: "13px", color: "#9ca3af" }}>
                    <strong style={{ color: "#fff" }}>{data?.total_influencers ?? 0}</strong> influencer{(data?.total_influencers ?? 0) === 1 ? "" : "s"}
                    {isFiltered && typeof data?.total_influencers_unfiltered === "number" && (
                        <span> of {data.total_influencers_unfiltered}</span>
                    )} {mode === INFLUENCER_MODE.FAVOURITES ? "saved" : "found"}
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <InfluencerPlatformToggle value={filters.platform} onChange={handlePlatformFilterChange} />
                    <button
                        onClick={() => setShowFilters(v => !v)}
                        className="influencer-btn-ghost"
                        style={activeFilterCount > 0 ? { color: "#F6255A", borderColor: "rgba(246,37,90,0.4)" } : undefined}
                    >
                        <SlidersHorizontal size={14} /> Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
                    </button>
                    <SortDropdown value={sort} options={INFLUENCER_SORT_OPTIONS} onChange={handleSortChange} />
                </div>
            </div>

            {/* §7 - saved influencers whose account has since left the
                store (reclassified as an official account on refresh).
                Dropped from the page itself; listed here whole since it's
                the only place a client can reach them to clear them. */}
            {mode === INFLUENCER_MODE.FAVOURITES && data?.missing > 0 && !dismissedMissing && (
                <div className="mb-3" style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)", borderRadius: "12px", padding: "14px 16px" }}>
                    <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                        <span style={{ display: "flex", alignItems: "center", gap: "8px", color: "#fbbf24", fontSize: "12.5px", fontWeight: 600 }}>
                            <HeartCrack size={14} /> {data.missing} saved influencer{data.missing === 1 ? "" : "s"} no longer available - they left the creator directory.
                        </span>
                        <button type="button" onClick={() => setDismissedMissing(true)} className="influencer-btn-ghost" style={{ padding: "4px 10px" }}>
                            Dismiss
                        </button>
                    </div>
                    <div className="d-flex flex-wrap gap-2 mt-2">
                        {(data.missing_entries || []).map((entry) => (
                            <button
                                key={`${entry.platform}:${entry.platform_key}`}
                                type="button"
                                onClick={() => handleClearMissingEntry(entry)}
                                style={{
                                    display: "flex", alignItems: "center", gap: "6px",
                                    background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
                                    color: "#e8e8e8", padding: "5px 10px", borderRadius: "8px",
                                    fontSize: "12px", fontFamily: "Rubik", cursor: "pointer",
                                }}
                            >
                                @{entry.username || entry.platform_key} <X size={12} />
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {showFilters && (
                <InfluencerFiltersPanel
                    filters={filters}
                    onChange={handleFiltersChange}
                    facets={data?.filter_options}
                    onClear={handleClearFilters}
                    activeCount={activeFilterCount}
                    onClose={() => setShowFilters(false)}
                />
            )}

            {/* Skeletons cover both the first-ever load (status still LOADING)
                and every later fetch that has no rows already on screen to
                dim instead - e.g. switching from an empty "My Favourites"
                into "All Influencers", or a filter/sort change while the
                current page is empty. Without the isFetching clause here,
                `status` (pinned at READY once reached - see
                useTopInfluencers) would let the "No results" empty-state
                below flash first, unstyled as a loading state, before real
                data arrives. Once there's a non-empty grid on screen, the
                dim-in-place overlay a few lines down handles it instead. */}
            {(status === INFLUENCER_STATUS.LOADING || (isFetching && influencers.length === 0)) && (
                <div className="influencer-grid">
                    {Array.from({ length: INFLUENCER_PAGE_SIZE }).map((_, i) => (
                        <div key={i} style={{ background: "#1c1d27", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "16px", padding: "16px" }}>
                            <Skeleton circle width={44} height={44} baseColor="#2a2a35" highlightColor="#33333f" />
                            <Skeleton width="80%" baseColor="#2a2a35" highlightColor="#33333f" style={{ marginTop: 10 }} />
                            <Skeleton width="60%" baseColor="#2a2a35" highlightColor="#33333f" />
                            <Skeleton width="100%" height={40} style={{ marginTop: 8 }} baseColor="#2a2a35" highlightColor="#33333f" />
                        </div>
                    ))}
                </div>
            )}

            {status === INFLUENCER_STATUS.READY && !isFetching && influencers.length === 0 && (
                <div className="text-center" style={{ background: "rgba(30,31,41,0.4)", borderRadius: "16px", border: "1px solid rgba(255,255,255,0.05)", padding: "56px 20px" }}>
                    <div style={{ width: "56px", height: "56px", background: "rgba(246,37,90,0.1)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                        <SearchX size={24} color="#F6255A" />
                    </div>
                    {isFiltered ? (
                        <>
                            <h6 style={{ color: "#fff", fontWeight: 600, marginBottom: "8px", fontSize: "16px" }}>
                                No {mode === INFLUENCER_MODE.FAVOURITES ? "saved influencers" : "influencers"} match these filters
                            </h6>
                            <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "13px", maxWidth: "380px", margin: "0 auto 16px", lineHeight: "1.5" }}>
                                {data?.total_influencers_unfiltered ?? 0} {mode === INFLUENCER_MODE.FAVOURITES ? "saved influencer" : "influencer"}{(data?.total_influencers_unfiltered ?? 0) === 1 ? "" : "s"} in total - try loosening a filter.
                            </p>
                            <button onClick={handleClearFilters} className="influencer-btn-outline">Clear Filters</button>
                        </>
                    ) : mode === INFLUENCER_MODE.FAVOURITES ? (
                        <>
                            <h6 style={{ color: "#fff", fontWeight: 600, marginBottom: "8px", fontSize: "16px" }}>No favourites yet</h6>
                            <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "13px", maxWidth: "380px", margin: "0 auto 16px", lineHeight: "1.5" }}>
                                Tap the heart on any creator to save them here.
                            </p>
                            <button onClick={() => handleModeChange(INFLUENCER_MODE.ALL)} className="influencer-btn-outline">Browse Influencers</button>
                        </>
                    ) : (
                        <>
                            <h6 style={{ color: "#fff", fontWeight: 600, marginBottom: "8px", fontSize: "16px" }}>No influencers found yet</h6>
                            <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "13px", maxWidth: "380px", margin: "0 auto", lineHeight: "1.5" }}>
                                We haven't discovered any creators yet - check back soon.
                            </p>
                        </>
                    )}
                </div>
            )}

            {status === INFLUENCER_STATUS.READY && influencers.length > 0 && (
                <>
                    <div
                        className="influencer-grid"
                        style={{
                            opacity: isFetching ? 0.45 : 1,
                            pointerEvents: isFetching ? "none" : "auto",
                            transition: "opacity 0.15s ease",
                        }}
                    >
                        {influencers.map(influencer => (
                            <InfluencerCard key={influencer.key} influencer={influencer} />
                        ))}
                    </div>

                    {(data?.has_previous || data?.has_next) && (
                        <div className="d-flex align-items-center justify-content-center gap-3 mt-4">
                            <button
                                onClick={() => {
                                    setNavigating("prev")
                                    setPage(p => {
                                        const nextPage = Math.max(1, p - 1)
                                        trackEvent(ANALYTICS_EVENTS.FEATURE.INFLUENCER_EXPLORER_PAGINATED, { page: nextPage })
                                        return nextPage
                                    })
                                    scrollToResultsTop()
                                }}
                                disabled={!data?.has_previous || isFetching}
                                className="influencer-btn-ghost"
                            >
                                {navigating === "prev" && isFetching ? <Spinner size="sm" /> : <ChevronLeft size={15} />} Prev
                            </button>
                            <span style={{ color: "#9ca3af", fontSize: "13px" }}>
                                Page {data?.page ?? page} of {data?.total_pages ?? 1}
                            </span>
                            <button
                                onClick={() => {
                                    setNavigating("next")
                                    setPage(p => {
                                        const nextPage = p + 1
                                        trackEvent(ANALYTICS_EVENTS.FEATURE.INFLUENCER_EXPLORER_PAGINATED, { page: nextPage })
                                        return nextPage
                                    })
                                    scrollToResultsTop()
                                }}
                                disabled={!data?.has_next || isFetching}
                                className="influencer-btn-ghost"
                            >
                                Next {navigating === "next" && isFetching ? <Spinner size="sm" /> : <ChevronRight size={15} />}
                            </button>
                        </div>
                    )}
                </>
            )}

            {status === INFLUENCER_STATUS.ERROR && (
                <div className="text-center" style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: "16px", padding: "40px 20px" }}>
                    <AlertTriangle size={28} color="#ef4444" style={{ marginBottom: "12px" }} />
                    <h6 style={{ color: "#fff", fontWeight: 600, marginBottom: "8px" }}>Something went wrong</h6>
                    <p style={{ color: "#9ca3af", fontSize: "13px", maxWidth: "420px", margin: "0 auto 16px" }}>
                        {typeof error === "string" ? error : "We couldn't load the influencer list."}
                    </p>
                    <button onClick={reload} className="influencer-btn-outline">Try Again</button>
                </div>
            )}

            <style>{`
                .influencer-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
                    gap: 18px;
                }
            `}</style>
        </div>
    )
}

export default InfluencerSection
